import React, { useMemo } from "react";
import { EmptyState } from "../../../../components/ui/PageBits";
import { fmtTime, isActiveBooking, todayKey } from "../../bookingService";
import { fmtLimit, effectiveTypeLimit, splitShopResources, shopTypes, excessShopTypes } from "../../bookingPlans";
import { BookingStatusPill } from "./ShopBits";

/** Shop landing: today's schedule, pending actions, plan usage. */
export default function ShopOverview({ shop, plans, resourceTypes, resources, bookings, onGoBookings, onGoCalendars }) {
    const tk = todayKey();
    const mine = useMemo(() => bookings.filter((b) => b.ownerId === shop.id), [bookings, shop.id]);
    const active = useMemo(() => mine.filter(isActiveBooking), [mine]);
    const today = useMemo(
        () => active.filter((b) => b.date === tk).sort((a, b) => a.start.localeCompare(b.start)),
        [active, tk]
    );
    const pending = useMemo(() => mine.filter((b) => b.status === "pending"), [mine]);
    const upcoming = useMemo(() => active.filter((b) => b.date >= tk).length, [active, tk]);

    const myTypes = shopTypes(resourceTypes, shop.id);
    const { limit, locked } = splitShopResources(shop, resources, plans);
    const excess = excessShopTypes(shop, resourceTypes, plans);
    const tLimit = effectiveTypeLimit(shop, plans);
    const suspended = shop.status !== "active";

    const resName = (id) => resources.find((r) => r.id === id)?.name || "(deleted)";

    return (
        <div className="space-y-4">
            {suspended && (
                <div className="hk-card" style={{ borderColor: "#fde68a", background: "#fffbeb" }}>
                    <strong>⛔ Shop suspended.</strong>
                    <span className="text-sm text-[#64748b]"> New bookings are rejected until the app owner reactivates this shop. Existing history is kept.</span>
                </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                    { label: "Today", value: today.length, sub: `${tk}`, icon: "📍" },
                    { label: "Upcoming", value: upcoming, sub: "active bookings", icon: "📅" },
                    { label: "Needs confirm", value: pending.length, sub: "pending requests", icon: "⏳" },
                    { label: "Resources", value: `${resources.filter((r) => r.ownerId === shop.id).length}/${fmtLimit(limit)}`, sub: `${myTypes.length}/${fmtLimit(tLimit)} calendars`, icon: "🪑" },
                ].map((t) => (
                    <div key={t.label} className="hk-card flex justify-between items-center">
                        <div>
                            <div className="hk-card-title" style={{ marginBottom: 4 }}>{t.label}</div>
                            <div className="text-3xl font-black hk-mono">{t.value}</div>
                            <div className="text-xs text-[#64748b] mt-1">{t.sub}</div>
                        </div>
                        <div className="w-11 h-11 rounded-2xl bg-[#f1f5f9] border border-[#cbd5e1] flex items-center justify-center text-xl shrink-0">
                            {t.icon}
                        </div>
                    </div>
                ))}
            </div>

            {(locked.length > 0 || excess.length > 0) && (
                <div className="hk-card" style={{ borderColor: "#fde68a", background: "#fffbeb" }}>
                    <span className="text-sm">
                        🔒 Over plan limits: {locked.length > 0 && <strong>{locked.length} resource(s) frozen ({locked.map((r) => r.name).join(", ")})</strong>}
                        {locked.length > 0 && excess.length > 0 && " · "}
                        {excess.length > 0 && <strong>{excess.length} calendar(s) read-only ({excess.map((t) => t.name).join(", ")})</strong>}.
                        {" "}Frozen items accept no new bookings until limits change.
                    </span>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                <div className="lg:col-span-7">
                    <div className="hk-card">
                        <div className="flex justify-between items-center mb-4">
                            <h5 className="hk-card-title" style={{ margin: 0 }}>Today's schedule</h5>
                            <button className="text-xs font-bold text-[#0f172a] hover:underline" onClick={onGoBookings}>
                                All bookings →
                            </button>
                        </div>
                        {today.length === 0 ? (
                            <EmptyState icon="🗓️" title="Nothing booked today" hint="New customer bookings will show up here." />
                        ) : (
                            <div className="space-y-2">
                                {today.map((b) => (
                                    <div key={b.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 rounded-xl border border-[#e2e8f0]">
                                        <div className="min-w-0">
                                            <div className="font-bold text-sm">{fmtTime(b.start)} – {fmtTime(b.end)} · {b.customer}</div>
                                            <div className="text-xs text-[#64748b]">{resName(b.resourceId)} · {b.type}</div>
                                        </div>
                                        <BookingStatusPill status={b.status} />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                <div className="lg:col-span-5">
                    <div className="hk-card">
                        <div className="flex justify-between items-center mb-4">
                            <h5 className="hk-card-title" style={{ margin: 0 }}>Pending requests · {pending.length}</h5>
                            <button className="text-xs font-bold text-[#0f172a] hover:underline" onClick={onGoBookings}>
                                Review →
                            </button>
                        </div>
                        {pending.length === 0 ? (
                            <EmptyState icon="✅" title="All caught up" hint="No bookings waiting for confirmation." />
                        ) : (
                            <div className="space-y-2">
                                {pending.slice(0, 5).map((b) => (
                                    <div key={b.id} className="px-4 py-3 rounded-xl bg-[#f8fafc] border border-[#cbd5e1]">
                                        <div className="font-bold text-sm">{b.customer}</div>
                                        <div className="text-xs text-[#64748b]">{b.date} · {fmtTime(b.start)} · {resName(b.resourceId)}</div>
                                    </div>
                                ))}
                                {pending.length > 5 && (
                                    <p className="text-xs text-[#64748b]">+ {pending.length - 5} more in Bookings.</p>
                                )}
                            </div>
                        )}
                        <button className="hk-btn hk-btn-outline hk-btn-sm w-full mt-4" onClick={onGoCalendars}>
                            Manage resources →
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
